import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  IconButton,
  Tooltip,
  alpha,
  useTheme
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import FMSModal from 'src/common/FMSModal';
import { getFormattedDate } from 'src/utils/getInitials';
import Loader from 'src/common/Loader';

/* ---------- Status Pill ---------- */
const StatusPill = ({ pending }) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      px: 1.5,
      py: 0.5,
      borderRadius: 10,
      bgcolor: pending
        ? alpha('#F97316', 0.1)
        : alpha('#10B981', 0.1),
      color: pending ? '#EA580C' : '#059669',
      border: `1px solid ${
        pending
          ? alpha('#F97316', 0.2)
          : alpha('#10B981', 0.2)
      }`
    }}
  >
    <Typography
      variant="caption"
      sx={{
        fontWeight: 700,
        textTransform: 'uppercase',
        fontSize: '0.65rem'
      }}
    >
      {pending ? 'Pending' : 'Synced'}
    </Typography>
  </Box>
);

StatusPill.propTypes = {
  pending: PropTypes.bool
};

function CardSyncStatusModal({
  open,
  onClose,
  loading,
  data,
  onRemoveDispenser,
  onDispenserClick
}) {
  const theme = useTheme();
  const history = useHistory();

  const [syncList, setSyncList] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedMapperId, setSelectedMapperId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setSyncList(data?.sync_status || []);
  }, [data]);

  const handleOpenDispenserDetails = (dispenserId) => {
    if (onDispenserClick) {
      onDispenserClick(dispenserId);
      return;
    }
    history.push(`/app/dispensers/${dispenserId}`);
  };

  /* ---------- SAFE DELETE HANDLER ---------- */
  const handleConfirmDelete = async () => {
    if (!selectedMapperId) return;

    try {
      setDeleting(true);

      await onRemoveDispenser(selectedMapperId);

      // Only update UI AFTER successful backend response
      setSyncList((prev) => prev.filter((d) => d.mapper_id !== selectedMapperId));

      setConfirmOpen(false);
      setSelectedMapperId(null);
    } catch (error) {
      console.error('Delete failed:', error);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <FMSModal open={open} maxWidth="md" onClose={onClose}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          CARD SYNC STATUS
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 3 }}>
          {loading ? (
            <Loader />
          ) : (
            <>
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 600 }}
                >
                  Card UID
                </Typography>
                <Typography fontWeight={700}>
                  {data?.uid || '-'}
                </Typography>
              </Box>

              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#F9FAFB' }}>
                      <TableCell sx={{ fontWeight: 700 }}>
                        Dispenser Name
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        Card Sync Status
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        Last Sync Date
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {syncList.length > 0 ? (
                      syncList.map((item) => {
                        const isPending = item.pending_sync === true
                          || !item.sync_status;

                        return (
                          <TableRow key={item.mapper_id}>
                            <TableCell>
                              <Typography
                                sx={{
                                  color: 'primary.main',
                                  cursor: 'pointer',
                                  fontWeight: 600,
                                  '&:hover': {
                                    textDecoration: 'underline'
                                  }
                                }}
                                onClick={() => handleOpenDispenserDetails(
                                  item.dispenser_id
                                )}
                              >
                                {item.dispenser || '-'}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <StatusPill pending={isPending} />
                            </TableCell>

                            <TableCell>
                              {item.synced_on
                                ? getFormattedDate(item.synced_on)
                                : '-'}
                            </TableCell>

                            <TableCell align="center">
                              <Tooltip title="Remove Dispenser">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => {
                                    setSelectedMapperId(item.mapper_id);
                                    setConfirmOpen(true);
                                  }}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                          <Typography color="text.secondary">
                            No sync data available
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>

        <Divider />

        <DialogActions sx={{ p: 2.5 }}>
          <Button variant="contained" onClick={onClose}>
            Close
          </Button>
        </DialogActions>
      </FMSModal>

      {/* Confirm Delete Modal */}
      <FMSModal
        open={confirmOpen}
        maxWidth="xs"
        onClose={() => setConfirmOpen(false)}
      >
        <DialogTitle>Remove Dispenser</DialogTitle>
        <Divider />
        <DialogContent>
          <Typography>
            Are you sure you want to remove this dispenser from the card?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} variant="contained" color="primary" borderRadius={1.5} textTransform="none" fontWeight={600} px={3}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={deleting}
          >
            Delete
          </Button>
        </DialogActions>
      </FMSModal>
    </>
  );
}

CardSyncStatusModal.propTypes = {
  open: PropTypes.bool.isRequired,
  loading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  data: PropTypes.object,
  onRemoveDispenser: PropTypes.func.isRequired,
  onDispenserClick: PropTypes.func
};

export default CardSyncStatusModal;
